import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { AVATAR_SIZE, Username, UsernameWrapper } from '@components/Feed/User/UserActivityDetail';
import type { DisplayConjunctionItem } from '@components/Feed/shared';
import { formatConjunction } from '@components/Feed/shared';
import { Avatar } from '@components/basics/Images/Avatar';
import { MinimalUser } from '@components/basics/MinimalUser';
import { Timestamp } from '@components/basics/Timestamp';
import { Button } from '@components/main-components/button/Button';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { UserHoverCard } from '@components/main-components/user/UserHoverCard';
import { ActivityCollectedByModal } from '@components/modals/CollectedBy/ActivityCollectedByModal';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, UsersActivityDetailFragmentDoc } from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import { getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';
import { UserHoverCardContent } from '../../../User/UserHoverCardContent';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment UsersActivityDetail on SongCollectedByManyAggregate {
    numCollectors
    featuredCollectors {
      user {
        id
        publicAddress
        artist {
          id
        }
        username
        webappUri
        avatar {
          id
          url
        }
        ...MinimalUser
      }
    }
  }
`);

export const UsersActivityDetail = ({
  activityFrag,
  activityId,
  timeStamp,
}: {
  activityFrag: FragmentType<UsersActivityDetailFragmentDoc>;
  activityId: string;
  timeStamp: number;
}) => {
  const { tablet } = MediaQuery.useContainer();
  const { openModal } = ModalContext.useContainer();

  const { featuredCollectors, numCollectors } = getFragment(
    UsersActivityDetailFragmentDoc,
    activityFrag,
  );

  const numFeaturedCollectors = featuredCollectors.length;
  const maxVisibleCollectors = tablet ? (numFeaturedCollectors > 3 ? 3 : numFeaturedCollectors) : 1;
  const totalOtherCollectors = numCollectors - maxVisibleCollectors;

  const collectors: DisplayConjunctionItem[] =
    featuredCollectors.length > 0
      ? featuredCollectors.slice(0, maxVisibleCollectors).map((collector, index) => {
          const isArtist = !!collector.user.artist;
          if (index === 0) {
            const hoverCardTrigger = (
              <div className="flex cursor-pointer gap-4">
                <div className="h-12 w-12 flex-shrink-0 rounded-full">
                  <Avatar
                    src={collector.user.avatar?.url}
                    username={collector.user.username}
                    borderRadius={1000}
                    size={AVATAR_SIZE}
                    walletAddress={collector.user.publicAddress}
                    hasBorder
                    webappUri={collector.user.webappUri}
                  />
                </div>
                <div className="flex h-[22px] min-w-0 items-center gap-[1px] pt-[1px] font-base text-base-l font-normal">
                  <AccessibleLink pathnameUri={webappUriPath(collector.user.webappUri)}>
                    <UsernameWrapper
                      css={{
                        '@tablet': {
                          maxWidth: '100%',
                        },
                      }}
                    >
                      <Username
                        css={{
                          maxWidth: 150,
                          '@tablet': {
                            maxWidth: 'unset',
                          },
                        }}
                      >
                        {collector.user.username}
                      </Username>
                    </UsernameWrapper>
                  </AccessibleLink>
                </div>
              </div>
            );
            return {
              id: collector.user.id,
              element: (
                <div className="flex cursor-pointer gap-1">
                  <UserHoverCard
                    userHoverCardContent={<UserHoverCardContent userId={collector.user.id} />}
                    trigger={hoverCardTrigger}
                  />
                  {isArtist && (
                    <div className="mt-0.5">
                      <VerifiedTooltip portal />
                    </div>
                  )}
                </div>
              ),
            };
          }

          if (index < maxVisibleCollectors) {
            return {
              id: collector.user.id,
              element: (
                <div
                  className="ml-0.5 flex h-[22px] min-w-0 items-center gap-[1px] pt-[1px] font-base text-base-l font-normal"
                  key={collector.user.id}
                >
                  <MinimalUser
                    userFrag={collector.user}
                    maxWidth={tablet ? 'unset' : 100}
                    avatarHasBorder
                    gap={4}
                    imagePriority={false}
                    borderRadius={100}
                  />
                </div>
              ),
            };
          }

          return {
            id: collector.user.id,
            element: null,
          };
        })
      : [];

  const others = numCollectors > maxVisibleCollectors && (
    <div className="-ml-[1px] flex h-[22px] min-w-0 items-center gap-[1px] pt-[1px] font-base text-base-l font-normal lg:ml-0">
      <Button
        variant="text"
        onClick={() => {
          openModal(ModalType.ACTIVITY_COLLECTED_BY, {
            body: <ActivityCollectedByModal activityId={activityId} />,
          });
        }}
        label={`${totalOtherCollectors} ${getSingularOrPlural({
          word: 'other',
          count: totalOtherCollectors,
        })}`}
        className="h-[22px] w-[unset] min-w-[unset] p-0 font-base text-base-l font-semibold"
      />
    </div>
  );

  const formatted = formatConjunction({
    items: !!others
      ? [...collectors, { id: 'other-users-activity-detail', element: others }]
      : collectors,
    css: {
      alignItems: 'unset',
      span: {
        marginTop: 2,
      },
    },
  });

  return (
    <div className="flex max-w-full flex-col">
      <div className="flex font-base text-base-l">
        <div className="flex cursor-pointer gap-4">{formatted?.[0] && formatted[0]}</div>
        {!!formatted && formatted?.length > 0 && formatted.slice(1)}
      </div>
      <div className="absolute flex translate-x-16 translate-y-7 items-center font-base text-base-m font-medium text-neutral500 lg:h-5">
        Minted a song
        <span>
          {SPACE}•{SPACE}
        </span>
        <Timestamp timeStamp={timeStamp} className="text-base-m font-normal text-neutral500" />
      </div>
    </div>
  );
};
